import { useCallback } from 'react';
import styled from '@emotion/styled';
import { Checkbox, tokens, PlanBadge, Stack } from '@hotjar/hotsauce';
import { usePlanForFeature } from '@hotjar/webapp/hooks/usePlanForFeature';
import { useGate } from '@hotjar/allowances';
import { useCurrentSite } from '@hotjar/site';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { useTranslation } from '@hotjar/translations';

const StyledStack = styled(Stack)`
  flex-flow: row wrap-reverse;
`;

// todo: margin-bottom can be removed once wizard is migrated :(
const StyledCheckbox = styled(Checkbox)`
  text-transform: initial;
  margin-right: ${tokens.spacer.x2};
  margin-bottom: 0 !important;
`;

const StyledPlanBadge = styled(PlanBadge)`
  margin: 2px 0;
`;

export interface BrandingToggleProps {
  hideBranding: boolean;
  isLogoSet: boolean;
  onChange: (newValue: boolean) => void;
}

export function BrandingToggle({
  hideBranding,
  isLogoSet,
  onChange,
}: BrandingToggleProps): JSX.Element {
  const { t } = useTranslation('feedback');
  const plan = usePlanForFeature('branding.toggle');
  const { data: currentSite } = useCurrentSite();
  const hasFeature = useGate('feedback.can_hide_branding', {
    id: `${currentSite?.id}`,
    type: 'site',
  });

  const onShowUpsell = useCallback(() => {
    if (currentSite !== undefined) upsell({ site: currentSite, referrer: 'polls.branding.toggle' });
  }, [currentSite]);

  function toggleBranding() {
    // check if branding wasn't already hidden - if so, allow for resigning from it
    if (!hideBranding && hasFeature !== true) {
      onShowUpsell();
      return;
    }
    onChange(!hideBranding);
  }

  return (
    <StyledStack direction="row">
      <StyledCheckbox
        id="feedback-widget-hide-branding"
        onChange={() => toggleBranding()}
        isChecked={isLogoSet || hideBranding}
        labelContent={t('wizard.section.appearance.branding.label')}
        isDisabled={isLogoSet}
      />
      {!isLogoSet && hasFeature !== true && plan && (
        <StyledPlanBadge kind={plan} size="s" onClick={onShowUpsell} />
      )}
    </StyledStack>
  );
}
